import { useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import type { Project, DiversSubProject } from '../types';
import {
  calculateWeeklyOvertime,
  resolveWeeklyHours,
  getISOYear
} from '../utils/overtimeUtils';
import { dateKey, getWeekNumber } from '../utils/dateUtils';
import { buildTimeEntriesToInsert } from '../utils/timesheetEntryBuilder';
import { computeSheetStatus, type DayStatuses } from '../utils/sheetStatusUtils';
import type {
  GridRow,
  DailyIndemnity,
  Ventilation,
} from '../pages/timesheet/types';

const MAX_DAILY_HOURS = 12;

interface UseTimesheetActionsParams {
  rows: GridRow[];
  setRows: React.Dispatch<React.SetStateAction<GridRow[]>>;
  indemnities: Record<string, DailyIndemnity>;
  setIndemnities: React.Dispatch<React.SetStateAction<Record<string, DailyIndemnity>>>;
  ventilations: Record<string, Ventilation>;
  setVentilations: React.Dispatch<React.SetStateAction<Record<string, Ventilation>>>;
  weekDays: Date[];
  trainingDays: string[];
  trainingProjectId: string;
  projects: Project[];
  diversSubProjects: DiversSubProject[];
  currentWeekStart: Date;
  effectiveUserId: string | undefined;
  isBE: boolean;
  isBE35h: boolean;
  isLocked: boolean;
  isLockedForDay: (day: Date) => boolean;
  isOutsideEntryMonth: (day: Date) => boolean;
  getGDProjectForDay: (dateStr: string) => Project | null;
  activeEntryMonth: { year: number; month: number } | undefined;
  dayStatuses: DayStatuses;
  setDayStatuses: React.Dispatch<React.SetStateAction<DayStatuses>>;
  customWeeklyHours: number | null;
  overrideWeeklyHours: number | null;
  setSaving: (saving: boolean) => void;
}

export function useTimesheetActions(params: UseTimesheetActionsParams) {
  const {
    rows, setRows, indemnities, setIndemnities,
    ventilations, setVentilations,
    weekDays, trainingDays, trainingProjectId,
    projects, diversSubProjects, currentWeekStart,
    effectiveUserId, isBE, isBE35h, isLocked, isLockedForDay,
    isOutsideEntryMonth, getGDProjectForDay,
    activeEntryMonth, dayStatuses, setDayStatuses,
    customWeeklyHours, overrideWeeklyHours, setSaving,
  } = params;

  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();
  const savingRef = useRef(false);

  const handleHoursChange = useCallback((rowIndex: number, dateStr: string, value: string) => {
    const dayDate = new Date(dateStr);
    if (isLockedForDay(dayDate)) return;
    const val = parseFloat(value) || 0;

    let currentDayTotal = val;
    rows.forEach((r, idx) => {
      if (idx !== rowIndex) {
        currentDayTotal += (r.hours[dateStr] || 0);
      }
    });

    if (currentDayTotal > MAX_DAILY_HOURS) {
      toast.warning(`Impossible : La limite légale est de ${MAX_DAILY_HOURS} heures par jour.`);
      return;
    }

    const newRows = [...rows];
    if (val > 0) newRows[rowIndex].hours[dateStr] = val;
    else {
        delete newRows[rowIndex].hours[dateStr];
        delete newRows[rowIndex].descriptions[dateStr];
    }
    setRows(newRows);
  }, [rows, setRows, isLockedForDay]);

  const handleRemoveProject = useCallback(async (rowIndex: number) => {
    if (isLocked) return;
    const row = rows[rowIndex];

    // Identifier les jours du mois actif vs l'autre mois (semaine à cheval)
    const activeDays: string[] = [];
    const otherDays: string[] = [];
    weekDays.forEach(day => {
      const dKey = dateKey(day);
      if (isOutsideEntryMonth(day)) {
        otherDays.push(dKey);
      } else {
        activeDays.push(dKey);
      }
    });

    const hasActiveHours = activeDays.some(dKey => (row.hours[dKey] || 0) > 0);
    const hasOtherHours = otherDays.some(dKey => (row.hours[dKey] || 0) > 0);

    if (hasActiveHours) {
      const msg = hasOtherHours
        ? `Retirer "${row.projectName}" du mois en cours ? Les heures de l'autre mois seront conservées.`
        : `Supprimer "${row.projectName}" et toutes ses heures saisies ?`;
      const ok = await confirmDialog({ title: 'Supprimer le projet', message: msg, variant: 'danger' });
      if (!ok) return;
    }

    if (hasOtherHours) {
      // Semaine à cheval : on vide uniquement les jours du mois actif
      const newRows = rows.map((r, idx) => {
        if (idx !== rowIndex) return r;
        const newHours = { ...r.hours };
        const newDescs = { ...r.descriptions };
        const newZones = { ...r.zones };
        activeDays.forEach(dKey => {
          delete newHours[dKey];
          delete newDescs[dKey];
          delete newZones[dKey];
        });
        return { ...r, hours: newHours, descriptions: newDescs, zones: newZones };
      });
      setRows(newRows);
      // Nettoyer les ventilations des jours actifs uniquement
      const newVentilations = { ...ventilations };
      activeDays.forEach(dKey => {
        delete newVentilations[`${row.projectId}_${dKey}`];
      });
      setVentilations(newVentilations);
    } else {
      // Pas d'heures sur l'autre mois : suppression complète de la ligne
      const newRows = rows.filter((_, idx) => idx !== rowIndex);
      setRows(newRows);
      const newVentilations = { ...ventilations };
      Object.keys(newVentilations).forEach(key => {
        if (key.startsWith(`${row.projectId}_`)) {
          delete newVentilations[key];
        }
      });
      setVentilations(newVentilations);
    }
  }, [rows, setRows, ventilations, setVentilations, weekDays, isLocked, isOutsideEntryMonth, confirmDialog]);

  const handleIndemnityChange = useCallback((dateStr: string, field: keyof DailyIndemnity, value: string | number | boolean) => {
    const dayDate = new Date(dateStr);
    if (isLockedForDay(dayDate)) return;
    const current = indemnities[dateStr] || { meals: 0, transport: 0, trajet: 0, is_grand_deplacement: false };
    const updated: DailyIndemnity = { ...current };

    if (field === 'meals') {
      const newMealValue = typeof value === 'string' ? parseInt(value) || 0 : (typeof value === 'number' ? value : 0);
      updated.meals = newMealValue;
    }
    else if (field === 'transport') updated.transport = typeof value === 'number' ? value : 0;
    else if (field === 'trajet') updated.trajet = typeof value === 'number' ? value : 0;
    else if (field === 'is_grand_deplacement') {
      const newGDValue = value as boolean;
      updated.is_grand_deplacement = newGDValue;
      if (newGDValue === true) {
        updated.transport = 0;
        updated.trajet = 0;
        const gdProject = getGDProjectForDay(dateStr);
        if (gdProject) {
          updated.gd_amount = gdProject.gd_amount;
          updated.gd_travel_hours = gdProject.gd_travel_hours;
        }
      } else {
        updated.gd_amount = undefined;
        updated.gd_travel_hours = undefined;
      }
    }

    setIndemnities({ ...indemnities, [dateStr]: updated });
  }, [indemnities, setIndemnities, isLockedForDay, getGDProjectForDay]);

  const calculateTotal = useCallback(() => {
    let total = 0;
    rows.forEach(r => Object.values(r.hours).forEach(h => total += h));
    total += (trainingDays.length * 7);
    return parseFloat(total.toFixed(2));
  }, [rows, trainingDays]);

  // --- SAUVEGARDE EN BASE ---
  const saveToDatabase = useCallback(async (status: 'draft' | 'pending', signatureData: string | null = null, pdfUrl: string | null = null) => {
    if (!effectiveUserId) return;
    // Guard anti-double-sauvegarde (race condition → doublons time_entries)
    if (savingRef.current) return;
    savingRef.current = true;

    if (isBE && status === 'pending') {
      // Guard ventilation BE : vérifie UNIQUEMENT les jours du mois actif
      // et non-locked. Sinon, on bloque l'employé en cheval qui aurait des
      // jours non-ventilés sur un mois déjà soumis (cas KHRIBACH).
      for (const row of rows) {
        for (const [dateStr2, hours] of Object.entries(row.hours)) {
          if (hours <= 0) continue;
          const dayDate = new Date(dateStr2);
          if (isOutsideEntryMonth(dayDate)) continue;
          if (isLockedForDay(dayDate)) continue;

          const key = `${row.projectId}_${dateStr2}`;
          const vent = ventilations[key];
          const totalVent = vent ? (vent.etude + vent.suivi + vent.mise_en_service + vent.chantier) : 0;
          if (Math.abs(totalVent - hours) > 0.01) {
            toast.error(`Erreur BE : Ventilation incorrecte pour ${row.projectName} au ${dateStr2}.`);
            // CRITIQUE : reset savingRef pour que l'employé puisse retenter
            // après avoir corrigé. Sans ça, tous les saves suivants étaient
            // bloqués silencieusement par le guard savingRef en début de fonction.
            savingRef.current = false;
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const year = getISOYear(currentWeekStart);
      const weekNumber = getWeekNumber(currentWeekStart);

      let { data: sheet } = await supabase
        .from('weekly_sheets')
        .select('id')
        .eq('employee_id', effectiveUserId)
        .eq('year', year)
        .eq('week_number', weekNumber)
        .maybeSingle();

      if (!sheet) {
        const { data: newSheet, error } = await supabase.from('weekly_sheets')
          .insert({ employee_id: effectiveUserId, year, week_number: weekNumber, status: 'draft' }).select().single();
        if (error) throw error;
        sheet = newSheet;
      }
      const sheetId = sheet!.id;

      // ─── Étape 1 : préparer le payload metadata pour UPDATE (sans exécuter) ─
      // On CALCULE tout le metadata du sheet ici mais on n'écrit RIEN avant
      // que la RPC entries+ventilations ait réussi. Sinon, si la RPC échoue
      // après l'UPDATE, on a une "feuille fantôme" : status=pending + signed
      // mais 0 entries (incident PAYSANT S21, 22/05/2026).
      let pendingSheetUpdate: Record<string, unknown> | null = null;
      let newDayStatusesForState: DayStatuses | null = null;

      if (status === 'pending') {
        const newDayStatuses = { ...dayStatuses };
        weekDays.forEach(day => {
          const dKey = dateKey(day);
          if (!isOutsideEntryMonth(day)) {
            newDayStatuses[dKey] = 'submitted';
          } else if (!newDayStatuses[dKey]) {
            newDayStatuses[dKey] = 'draft';
          }
        });

        // Rétrocompatibilité : calculer submitted_months depuis dayStatuses
        const monthsSet = new Set<string>();
        Object.entries(newDayStatuses).forEach(([ds, st]) => {
          if (st === 'submitted') {
            const d = new Date(ds);
            monthsSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }
        });
        const newSubmittedMonths = Array.from(monthsSet);

        const weekNumForSave = getWeekNumber(currentWeekStart);
        let normalHoursForSave = (trainingDays.length * 7);
        let saturdayHoursForSave = 0;
        let sundayHoursForSave = 0;

        rows.forEach(r => {
          Object.entries(r.hours).forEach(([ds, h]) => {
            const dayDate = new Date(ds);
            const dayOfWeek = dayDate.getDay();
            if (dayOfWeek === 0) sundayHoursForSave += h;
            else if (dayOfWeek === 6) saturdayHoursForSave += h;
            else normalHoursForSave += h;
          });
        });

        const hasGDForSave = Object.values(indemnities).some(ind => ind?.is_grand_deplacement === true);

        const hoursLuSaForSave = normalHoursForSave + saturdayHoursForSave;
        const effectiveHoursForSave = resolveWeeklyHours(overrideWeeklyHours, customWeeklyHours);
        const overtimeForSave = calculateWeeklyOvertime(weekNumForSave, hoursLuSaForSave, sundayHoursForSave, hasGDForSave || isBE35h, effectiveHoursForSave);

        // `status` dérivé : trigger DB le re-calculerait de toute façon, mais
        // on l'envoie pour rester explicite côté code.
        const derivedStatus = computeSheetStatus(newDayStatuses, null);

        pendingSheetUpdate = {
          status: derivedStatus,
          submitted_months: newSubmittedMonths,
          day_statuses: newDayStatuses,
          signature_url: signatureData,
          pdf_url: pdfUrl,
          signed_at: new Date().toISOString(),
          total_hours: calculateTotal(),
          week_start: currentWeekStart.toISOString(),
          overtime_25: overtimeForSave.overtime25,
          overtime_50: overtimeForSave.overtime50,
          sunday_hours: overtimeForSave.sundayHours,
          regular_hours: overtimeForSave.regularHours,
          rejection_reason: null,
          rejected_at: null,
        };
        newDayStatusesForState = newDayStatuses;
      }

      // ─── Étape 2 : construire les entries (fonction pure) ─────────────────
      const { entriesToInsert, ventilationMap } = buildTimeEntriesToInsert({
        rows,
        indemnities,
        ventilations,
        trainingDays,
        trainingProjectId,
        projects,
        diversSubProjects,
        sheetId,
        isBE,
        isLockedForDay,
        isOutsideEntryMonth,
      });

      // ─── GUARD SÉMANTIQUE anti-corruption ─────────────────────────────────
      // Si l'employé soumet une feuille avec total_hours déclaré > 0 mais
      // que la construction des entries renvoie une liste vide, c'est une
      // incohérence (probablement un bug de filtrage isLockedForDay /
      // isOutsideEntryMonth, ou rows vidés par un effet React). On REFUSE
      // d'écrire en DB pour ne pas reproduire l'incident CORDIER S17 (PDF
      // signé + 0 entries).
      const declaredTotal = calculateTotal();
      if (status === 'pending' && declaredTotal > 0 && entriesToInsert.length === 0) {
        throw new Error(
          "Incohérence détectée : total déclaré de " + declaredTotal + "h " +
          "mais aucune ligne à enregistrer. Sauvegarde annulée pour protéger " +
          "vos données. Veuillez recharger la page et ressaisir vos heures."
        );
      }

      // ─── Construction du payload ventilations BE pour la RPC ──────────────
      // Format : [{ project_id, work_date, work_type, hours, divers_sub_project_id? }, ...]
      // La clé du ventilationMap encode les 3 dimensions séparées par `|` :
      // `${actualProjectId}|${diversSubProjectId ?? ''}|${workDate}`
      // pour distinguer 2 sous-projets divers même parent même jour (cf
      // CORDIER S19 et migration 20260519).
      const ventilationsPayload: Array<{
        project_id: string;
        work_date: string;
        work_type: 'etude' | 'suivi' | 'mise_en_service' | 'chantier';
        hours: number;
        divers_sub_project_id: string | null;
      }> = [];
      if (isBE) {
        for (const [key, vent] of Object.entries(ventilationMap)) {
          const [projectId, subIdRaw, workDate] = key.split('|');
          const diversSubProjectId = subIdRaw ? subIdRaw : null;
          const base = { project_id: projectId, work_date: workDate, divers_sub_project_id: diversSubProjectId };
          if (vent.etude > 0)            ventilationsPayload.push({ ...base, work_type: 'etude',           hours: vent.etude });
          if (vent.suivi > 0)            ventilationsPayload.push({ ...base, work_type: 'suivi',           hours: vent.suivi });
          if (vent.mise_en_service > 0)  ventilationsPayload.push({ ...base, work_type: 'mise_en_service', hours: vent.mise_en_service });
          if (vent.chantier > 0)         ventilationsPayload.push({ ...base, work_type: 'chantier',        hours: vent.chantier });
        }
      }

      // ─── Liste des dates "à remplacer" (jours du mois actif non-verrouillés) ─
      // Le RPC supprimera les entries existantes pour ces dates puis insérera
      // les nouvelles, le tout dans une seule transaction PG.
      const replaceDates: string[] = [];
      for (const day of weekDays) {
        if (isLockedForDay(day)) continue;
        if (isOutsideEntryMonth(day)) continue;
        replaceDates.push(dateKey(day));
      }

      // ─── APPEL ATOMIQUE : DELETE + INSERT entries + INSERT ventilations ────
      // En cas d'échec à n'importe quelle étape (RLS, contrainte, réseau,
      // exception PG), Postgres rollback la totalité. Plus de fenêtre entre
      // DELETE et INSERT où une coupure réseau peut détruire les données.
      const { error: rpcError } = await supabase.rpc('save_time_entries_atomic', {
        p_sheet_id:      sheetId,
        p_replace_dates: replaceDates,
        p_entries:       entriesToInsert,
        p_ventilations:  ventilationsPayload,
      });
      if (rpcError) {
        // Wrap explicite en Error pour préserver le message dans le catch
        // englobant (qui sinon retomberait sur 'Erreur inconnue').
        throw new Error(rpcError.message || 'Erreur RPC inconnue');
      }

      // ─── Étape 3 : UPDATE metadata sheet APRÈS succès RPC ─────────────────
      // Inversion d'ordre cruciale par rapport à l'ancien flux (où UPDATE
      // précédait RPC). Si la RPC avait échoué après UPDATE → corruption :
      // sheet status=pending + signed mais 0 entries (incident PAYSANT S21,
      // 22/05/2026). Ici, si on arrive ici c'est que la RPC a réussi (entries
      // committées), donc l'UPDATE peut s'exécuter en toute sécurité.
      // Si l'UPDATE échoue malgré tout (RLS, réseau), les entries sont en
      // base mais le sheet reste en draft → état "à finaliser", pas une
      // corruption. L'employé voit l'erreur et retente.
      if (pendingSheetUpdate) {
        const { data: updatedSheets, error: updateError } = await supabase
          .from('weekly_sheets').update(pendingSheetUpdate)
          .eq('id', sheetId).select('id');
        if (updateError) throw updateError;
        if (!updatedSheets || updatedSheets.length === 0) {
          throw new Error("Mise à jour refusée par la sécurité (RLS). La feuille est peut-être déjà validée — contactez votre admin pour la débloquer.");
        }
        if (newDayStatusesForState) setDayStatuses(newDayStatusesForState);
      }

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      throw new Error(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [
    effectiveUserId, isBE, isBE35h, rows, ventilations, indemnities,
    currentWeekStart, activeEntryMonth, dayStatuses, setDayStatuses, isOutsideEntryMonth,
    trainingDays, trainingProjectId, projects, diversSubProjects,
    customWeeklyHours, overrideWeeklyHours, isLockedForDay, calculateTotal, setSaving
  ]);

  const handleSaveDraft = useCallback(async (onSuccess?: () => void) => {
    try {
      await saveToDatabase('draft');
      if (onSuccess) onSuccess();
      else toast.success("Brouillon enregistré avec succès !");
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde : " + e);
    }
  }, [saveToDatabase, toast]);

  // ─────────────────────────────────────────────────────────────────────────
  // submitEmptyMonth — Soumettre un mois sans heures (semaine à cheval)
  //
  // Cas d'usage : un employé travaille la première moitié de la semaine
  // (ex: lun-jeu d'avril) puis ne travaillera pas la seconde moitié
  // (ven-dim de mai). Avant ce mécanisme, la feuille restait coincée dans
  // "En cours" tant qu'il n'avait pas explicitement soumis mai.
  //
  // Ce handler marque tous les jours non-verrouillés du mois actif comme
  // 'submitted' dans day_statuses (sans créer de time_entry), met à jour
  // le status dérivé, et purge via la RPC atomique d'éventuelles entries
  // orphelines sur ces jours.
  //
  // Le trigger DB compute_weekly_sheet_status n'étant PAS déployé en prod,
  // on calcule le status côté client avant l'UPDATE pour garantir la
  // cohérence.
  // ─────────────────────────────────────────────────────────────────────────
  const submitEmptyMonth = useCallback(async (): Promise<void> => {
    if (!effectiveUserId || !activeEntryMonth) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      const year = getISOYear(currentWeekStart);
      const weekNumber = getWeekNumber(currentWeekStart);

      // 1. Trouver ou créer la feuille
      let { data: sheet } = await supabase
        .from('weekly_sheets')
        .select('id')
        .eq('employee_id', effectiveUserId)
        .eq('year', year)
        .eq('week_number', weekNumber)
        .maybeSingle();

      if (!sheet) {
        const { data: newSheet, error } = await supabase.from('weekly_sheets')
          .insert({ employee_id: effectiveUserId, year, week_number: weekNumber, status: 'draft' })
          .select().single();
        if (error) throw error;
        sheet = newSheet;
      }
      const sheetId = sheet!.id;

      // 2. Construire les nouveaux day_statuses : tous les jours du mois actif
      //    passent à 'submitted' (sauf ceux déjà 'validated' qu'on respecte).
      const newDayStatuses = { ...dayStatuses };
      weekDays.forEach(day => {
        const dKey = dateKey(day);
        const inActiveMonth =
          day.getFullYear() === activeEntryMonth.year &&
          day.getMonth() === activeEntryMonth.month;
        if (inActiveMonth) {
          if (newDayStatuses[dKey] === 'validated') return;
          newDayStatuses[dKey] = 'submitted';
        } else if (!newDayStatuses[dKey]) {
          newDayStatuses[dKey] = 'draft';
        }
      });

      // 3. Recalculer submitted_months (rétrocompat colonne legacy)
      const monthsSet = new Set<string>();
      Object.entries(newDayStatuses).forEach(([ds, st]) => {
        if (st === 'submitted') {
          const d = new Date(ds);
          monthsSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
      });
      const newSubmittedMonths = Array.from(monthsSet);

      // 4. Calculer le status dérivé côté client (trigger DB non déployé en prod)
      const derivedStatus = computeSheetStatus(newDayStatuses, null);

      // 5. UPDATE weekly_sheets avec le status recalculé
      const { data: updatedSheets, error: updateError } = await supabase
        .from('weekly_sheets').update({
          status: derivedStatus,
          day_statuses: newDayStatuses,
          submitted_months: newSubmittedMonths,
        }).eq('id', sheetId).select('id');
      if (updateError) throw updateError;
      if (!updatedSheets || updatedSheets.length === 0) {
        throw new Error("Mise à jour refusée par la sécurité (RLS).");
      }

      // 6. Purger via RPC atomique d'éventuelles entries orphelines du mois actif
      const replaceDates: string[] = [];
      for (const day of weekDays) {
        if (isLockedForDay(day)) continue;
        const inActiveMonth =
          day.getFullYear() === activeEntryMonth.year &&
          day.getMonth() === activeEntryMonth.month;
        if (inActiveMonth) replaceDates.push(dateKey(day));
      }

      if (replaceDates.length > 0) {
        const { error: rpcError } = await supabase.rpc('save_time_entries_atomic', {
          p_sheet_id:      sheetId,
          p_replace_dates: replaceDates,
          p_entries:       [],
          p_ventilations:  [],
        });
        if (rpcError) throw new Error(rpcError.message || 'Erreur RPC inconnue');
      }

      setDayStatuses(newDayStatuses);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      throw new Error(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [
    effectiveUserId, activeEntryMonth, currentWeekStart, weekDays,
    dayStatuses, setDayStatuses, isLockedForDay, setSaving,
  ]);

  return {
    handleHoursChange,
    handleRemoveProject,
    handleIndemnityChange,
    calculateTotal,
    saveToDatabase,
    handleSaveDraft,
    submitEmptyMonth,
  };
}
